import { getApiOrigin } from "@utils/apiOrigin";
import { authenticatedFetch } from "@utils/authenticatedFetch";
import { getCurrentUserId } from "@utils/userUtils";

const COMPANY_SETUP_CACHE_KEY = "unicomply:companySetupDone";

export interface CompanySetupStatus {
  has_company: boolean;
  email?: string | null;
}

export interface CompanySetupResult {
  customer_id: number;
  org_id?: number | null;
  org_name?: string | null;
  company_name: string;
}

function companySetupCacheUserId(
  userId?: string | number | null,
): string | null {
  const id = String(userId ?? getCurrentUserId() ?? "").trim();
  if (!id || id === "0") return null;
  return id;
}

export function markCompanySetupDone(userId?: string | number | null): void {
  const id = companySetupCacheUserId(userId);
  if (!id) return;
  try {
    localStorage.setItem(`${COMPANY_SETUP_CACHE_KEY}:${id}`, "1");
  } catch {
    // ignore quota / private mode
  }
}

export function clearCompanySetupCache(userId?: string | number | null): void {
  const id = companySetupCacheUserId(userId);
  if (!id) return;
  try {
    localStorage.removeItem(`${COMPANY_SETUP_CACHE_KEY}:${id}`);
  } catch {
    // ignore quota / private mode
  }
}

export function hasCachedCompanySetup(userId?: string | number | null): boolean {
  const id = companySetupCacheUserId(userId);
  if (!id) return false;
  try {
    return localStorage.getItem(`${COMPANY_SETUP_CACHE_KEY}:${id}`) === "1";
  } catch {
    return false;
  }
}

export async function getCompanySetupStatus(): Promise<CompanySetupStatus> {
  const cached = hasCachedCompanySetup();
  const base = getApiOrigin();
  const response = await authenticatedFetch(`${base}/api/v1/company/setup-status`);
  if (!response.ok) {
    if (cached) {
      return { has_company: true };
    }
    throw new Error(`Company setup status failed (${response.status})`);
  }
  const body = await response.json();
  const status = (body?.data || body) as CompanySetupStatus;
  if (status.has_company) {
    markCompanySetupDone();
  } else {
    clearCompanySetupCache();
  }
  return status;
}

export async function createCompanySetup(payload: {
  company_name: string;
  phone: string;
  email: string;
}): Promise<CompanySetupResult> {
  const base = getApiOrigin();
  const response = await authenticatedFetch(`${base}/api/v1/company/setup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body?.success === false) {
    const detail = body?.detail || body?.error || `HTTP ${response.status}`;
    throw new Error(
      typeof detail === "string" ? detail : detail?.message || JSON.stringify(detail),
    );
  }
  markCompanySetupDone();
  return (body?.data || body) as CompanySetupResult;
}
